import { ContributionTier } from '@/types/donate'

/**
 * Contribution tier definitions
 *
 * @remarks
 * Charter Founding Member tier has time-limited availability (expires March 1, 2026).
 * After expiration, getActiveTiers() will automatically exclude it from display.
 *
 * Benefit Fulfillment Workflow:
 * 1. Donor completes contribution via Zeffy embedded form
 * 2. Zeffy captures donor information (name, email, address, amount)
 * 3. NCESP staff export donor data from Zeffy dashboard monthly
 * 4. Charter members ($250+) receive:
 *    - Personalized certificate mailed within 30 days
 *    - Limited-edition membership pin included with certificate
 *    - Name added to website recognition page within 14 days
 * 5. Gold Circle ($500+) and President's Club ($1000+) members:
 *    - Added to appropriate recognition tier on website
 *    - President's Club: Personal thank you call/email from leadership within 7 days
 *
 * Update expirationDate for Charter Founding Member tier as needed.
 */
export const contributionTiers: ContributionTier[] = [
  {
    id: 'general',
    name: 'General Supporters',
    minAmount: 25,
    description:
      'Every contribution makes a difference in advancing our mission',
    benefits: [
      'Periodic appreciation drawings for supporters donating $25 or more',
      'Email updates on legislative progress and events',
      'Recognition as valued NCESP supporter',
    ],
    icon: 'VolunteerActivism',
    isActive: true,
  },
  {
    id: 'charter',
    name: 'Charter Founding Member',
    minAmount: 250,
    description:
      'Limited-time designation honoring those who helped lay the foundation',
    benefits: [
      'Personalized commemorative certificate',
      'Limited-edition membership pin',
      'Permanent name recognition on NCESP website',
      'All General Supporter benefits',
    ],
    icon: 'EmojiEvents',
    expirationDate: new Date('2026-03-01'), // 90 days from assumed 2025-12-01 launch
    isActive: true,
    featured: true, // Visually highlight to create urgency
  },
  {
    id: 'gold',
    name: 'Gold Circle',
    minAmount: 500,
    description: "Accelerate NCESP's long-term growth and advocacy impact",
    benefits: [
      'Recognition as Gold Circle member on website',
      'Special acknowledgment in annual reports',
      'Priority access to advocacy resources',
      'All General Supporter benefits',
    ],
    icon: 'Star',
    isActive: true,
  },
  {
    id: 'presidents',
    name: "President's Club",
    minAmount: 1000,
    description: 'Elite recognition with insider access to NCESP initiatives',
    benefits: [
      'Periodic insider briefings on NCESP strategy and progress',
      "Recognition as President's Club member on website",
      'Personal thank you from NCESP leadership',
      'Invitation to exclusive annual briefing (virtual or in-person)',
      'All Gold Circle benefits',
    ],
    icon: 'WorkspacePremium',
    isActive: true,
  },
]

/**
 * Get currently active tiers based on expiration dates and manual overrides
 *
 * @returns Array of active tiers filtered by:
 *   1. Manual override: tier.isActive === false excludes tier regardless of expiration
 *   2. Expiration check: tier.expirationDate must be in the future
 *   3. No expiration: tiers without expirationDate are always included
 *
 * @example
 * // Charter tier active before March 1, 2026
 * const activeTiers = getActiveTiers()
 * // Returns all 4 tiers if current date < 2026-03-01
 *
 * @example
 * // Charter tier expired after March 1, 2026
 * const activeTiers = getActiveTiers()
 * // Returns 3 tiers (General, Gold, President's) if current date >= 2026-03-01
 */
export const getActiveTiers = (): ContributionTier[] => {
  return contributionTiers.filter(tier => {
    // Check manual override first
    if (tier.isActive === false) return false

    // Check expiration date
    if (tier.expirationDate) {
      return new Date() < tier.expirationDate
    }

    // No expiration date means always active
    return true
  })
}

/**
 * Get tier by ID
 */
export const getTierById = (id: string): ContributionTier | undefined => {
  return contributionTiers.find(tier => tier.id === id)
}

/**
 * Check if Charter Founding Member tier is still available
 */
export const isCharterMemberAvailable = (): boolean => {
  const charterTier = getTierById('charter')
  if (!charterTier) return false
  if (charterTier.isActive === false) return false
  if (charterTier.expirationDate) {
    return new Date() < charterTier.expirationDate
  }
  return true
}
